<template>
  <div class="site-footer">
    <div class="site-footer__main grid no-gap">
      <div class="site-footer__newsletter container col xs4 l5">
        <span
          v-if="liquid.settings.show_newsletter_ui"
          class="
            site-footer__newsletter-message
            text-body-s
            text-body-m-desktop
          "
          v-text="$string('general.site_footer.sign_up')"
        />

        <slot
          v-if="liquid.settings.show_newsletter_ui"
          name="newsletter"
        />

        <social-icons />
      </div>

      <div class="site-footer__nav col l4">
        <div class="site-footer__nav-col">
          <span
            v-if="liquid.settings.nav_menu_title_1"
            class="site-footer__nav-title text-body-sbold"
            v-text="liquid.settings.nav_menu_title_1"
          />

          <div
            v-if="nav1.items.length > 0"
            class="site-footer__nav-container"
          >
            <a
              v-for="link in nav1.items"
              :key="link.handle"
              class="site-footer__link"
              :href="link.url"
              @click="footerLinkDataLayer(link.title)"
              v-text="link.title"
            />
          </div>
        </div>

        <div class="site-footer__nav-col">
          <span
            v-if="liquid.settings.nav_menu_title_2"
            class="site-footer__nav-title text-body-sbold"
            v-text="liquid.settings.nav_menu_title_2"
          />

          <div
            v-if="nav2.items.length > 0"
            class="site-footer__nav-container"
          >
            <a
              v-for="link in nav2.items"
              :key="link.handle"
              class="site-footer__link"
              :href="link.url"
              @click="footerLinkDataLayer(link.title)"
              v-text="link.title"
            />
          </div>
        </div>
      </div>

      <div class="site-footer__info container col xs4 l3">
        <div
          v-if="liquid.settings.find_store"
          class="site-footer__locator"
        >
          <icon-store />
          <span
            v-html="liquid.settings.find_store"
          />
        </div>

        <div
          v-if="liquid.settings.find_store"
          class="site-footer__phone"
        >
          <icon-phone />
          <span v-html="liquid.settings.telephone" />
        </div>

        <basic-accordion
          v-if="nav1.items.length > 0"
          id="menu-1"
          :active="getActiveState('menu-1')"
          :title="liquid.settings.nav_menu_title_1"
          block-class="site-footer"
          @toggle-accordion="toggleAccordion"
        >
          <template #content>
            <a
              v-for="link in nav1.items"
              :key="link.handle"
              class="site-footer__link text-body-xs"
              :href="link.url"
              v-text="link.title"
            />
          </template>
        </basic-accordion>

        <basic-accordion
          v-if="nav2.items.length > 0"
          id="menu-2"
          :active="getActiveState('menu-2')"
          :title="liquid.settings.nav_menu_title_2"
          block-class="site-footer"
          @toggle-accordion="toggleAccordion"
        >
          <template #content>
            <a
              v-for="link in nav2.items"
              :key="link.handle"
              class="site-footer__link text-body-xs"
              :href="link.url"
              v-text="link.title"
            />
          </template>
        </basic-accordion>

        <basic-accordion
          v-if="legal.items.length > 0"
          id="legal-menu"
          :active="getActiveState('legal-menu')"
          :title="liquid.settings.legal_menu_title"
          block-class="site-footer"
          @toggle-accordion="toggleAccordion"
        >
          <template #content>
            <a
              v-for="link in legal.items"
              :key="link.handle"
              class="site-footer__link text-body-xs"
              :href="link.url"
              v-text="link.title"
            />
          </template>
        </basic-accordion>

        <social-icons />

        <!-- Country Switcher goes here -->

        <span
          v-if="liquid.settings.app_download_title"
          class="site-footer__download text-body-xs"
          v-text="liquid.settings.app_download_title"
        />

        <div class="site-footer__app-links">
          <a
            v-if="liquid.settings.apple_download_link"
            :href="liquid.settings.apple_download_link"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.app_store_icons.apple_app_store')"
            />
            <icon-applestore />
          </a>

          <a
            v-if="liquid.settings.google_download_link"
            :href="liquid.settings.google_download_link"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.app_store_icons.google_play_store')"
            />
            <icon-googleplay />
          </a>
        </div>
      </div>
    </div>

    <div class="site-footer__legal col xs4 l12">
      <div class="container">
        <div class="site-footer__legal-left">
          <payment-icons />

          <icon-alfuttaim />

          <span
            class="site-footer__copyright text-body-xs"
            v-text="$string('general.site_footer.copyright', {
              replace: {
                current_year: new Date().getFullYear(),
              }
            })"
          />
        </div>

        <div class="site-footer__legal-right">
          <payment-icons />

          <div
            v-if="legal.items.length > 0"
            class="site-footer__nav-row"
          >
            <a
              v-for="link in legal.items"
              :key="link.handle"
              class="site-footer__link"
              :href="link.url"
              @click="footerLinkDataLayer(link.title)"
              v-text="link.title"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Site footer (site-footer)
 * -----------------------------------------------------------------------------
 * Site Footer.
 *
 * @param {String} liquid - Section's liquid scheme data.
 * @param {Object} nav1 - Navigation LinkList 1's data.
 * @param {Object} nav2 - Navigation LinkList 2's data.
 * @param {Object} legal - Legal LinkList's data.
 *
 */
import BasicAccordion from '~async/basic-accordion/basic-accordion'
import PaymentIcons from '~async/payment-icons/payment-icons'
import SocialIcons from '~async/social-icons/social-icons'

import IconApplestore from '~icons/misc/applestore.svg'
import IconGoogleplay from '~icons/misc/googleplay.svg'
import IconAlfuttaim from '~icons/misc/alfuttaim.svg'
import IconStore from '~icons/misc/store.svg'
import IconPhone from '~icons/misc/phone.svg'

export default {
  name: 'SiteFooter',

  components: {
    BasicAccordion,
    PaymentIcons,
    SocialIcons,
    IconApplestore,
    IconGoogleplay,
    IconAlfuttaim,
    IconStore,
    IconPhone,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    nav1: {
      type: Object,
      default: () => ({
        handle: '',
        items: [],
      }),
    },
    nav2: {
      type: Object,
      default: () => ({
        handle: '',
        items: [],
      }),
    },
    legal: {
      type: Object,
      default: () => ({
        handle: '',
        items: [],
      }),
    },
  },

  data() {
    return {
      accordions: [
        {
          id: 'menu-1',
          active: false,
        },
        {
          id: 'menu-2',
          active: false,
        },
        {
          id: 'legal-menu',
          active: false,
        },
      ],
    }
  },

  mounted() {
    this.setNodeSelectors()
    this.footerNewsletterSuccessDataLayer()
  },

  methods: {

    /**
     * Set node selectors.
     */
    setNodeSelectors() {
      this.nodeSelectors = {
        newsletterSuccess: document.querySelector('[js-site-footer="newsletterSuccess"]'),
      }
    },

    /**
     * Toggle accordion's active state.
     * @param {String} id - Id of accordion.
     */
    toggleAccordion(id) {
      const accordion = this.accordions.find(item => item.id === id)

      if (accordion.active) {
        accordion.active = false
        return
      }

      this.accordions.forEach((accordion) => {
        if (accordion.id === id) {
          accordion.active = true
          return
        }

        accordion.active = false
      })
    },

    /**
     * Get accordion's active state.
     * @param {String} id - Id of accordion.
     */
    getActiveState(id) {
      const accordion = this.accordions.find(item => item.id === id)

      return accordion.active
    },

    /**
     * Send the footer link click event info to the data layer.
     * @param {String} label - clicked link title.
     */
    footerLinkDataLayer(label) {
      cnvs.EventBus.emit('Datalayer:footerLink', label.toLowerCase())
    },

    /**
     * Send the successful newsletter subscription event info to the data layer.
     */
    footerNewsletterSuccessDataLayer() {
      if (!this.nodeSelectors.newsletterSuccess) {
        return
      }
      cnvs.EventBus.emit('Datalayer:footerNewsletterSuccess')
    },

  },
}
</script>
